import {
  type JSONSchemaDefinition,
  metadataPathKey,
  type PriceModelInputMetadataEntry,
} from "@forgepoint/shared";
import { JSONSchemaFieldTemplate } from "@/components/jsonSchemaForm/JSONSchemaFieldTemplate";
import type {
  JSONObject,
  JSONSchemaFieldTemplateProps,
  JSONValue,
} from "@/components/jsonSchemaForm/types";
import { isJSONObject } from "@/components/jsonSchemaForm/types";
import { PriceModelInputReasoning } from "./PriceModelInputReasoning";
import { PriceModelInputRevert } from "./PriceModelInputRevert";
import { readValueAtPath } from "./priceModelInputMetadata";

/** Form context supplied to the price-model input field template. */
export interface PriceModelInputFormContext {
  metadataByPath: Map<string, PriceModelInputMetadataEntry>;
  /** Path keys whose value currently differs from the last-saved inputs. */
  locallyEditedPaths: Set<string>;
  priceModelName: string;
  /**
   * The price model's effective defaults: schema-level defaults merged with its
   * `defaultInputs`, keyed by top-level input name.
   */
  effectiveDefaults: JSONObject;
  /** The last-saved inputs the metadata describes, restored on revert. */
  baselineInputs: JSONValue;
  /**
   * Puts the last-saved value back at a path.
   * @param schemaPath - The path addressing the field to restore
   */
  revertPath: (schemaPath: string[]) => void;
}

/**
 * Field template for price model inputs: renders the default field, plus a
 * sparkle revealing the agent's reasoning on fields it populated, a revert
 * control on edited fields whose saved value carried that reasoning, or a muted
 * hint on fields still sitting at their price-model default.
 * @param props - The standard field template props, carrying the metadata form context
 * @returns The rendered field with its provenance affordance
 */
export function PriceModelInputFieldTemplate(
  props: JSONSchemaFieldTemplateProps<PriceModelInputFormContext>,
) {
  const { schemaPath, formContext, children, schema, currValue, defaultValue } =
    props;

  if (!formContext || isContainerSchema(schema)) {
    return <JSONSchemaFieldTemplate {...props} />;
  }

  const pathKey = metadataPathKey(schemaPath);
  const isLocallyEdited = formContext.locallyEditedPaths.has(pathKey);
  const entry = formContext.metadataByPath.get(pathKey);
  const agentReasoning = getAgentReasoning(entry);
  const reasoning = isLocallyEdited ? undefined : agentReasoning;
  // Revert is offered only where it brings reasoning back, not as a general
  // undo on every edited field.
  const canRevert = isLocallyEdited && agentReasoning !== undefined;
  const fieldLabel = props.label ?? schemaPath[schemaPath.length - 1];
  const showsDefaultHint =
    !isLocallyEdited &&
    !entry &&
    isAtPriceModelDefault({
      schema,
      schemaPath,
      currValue,
      defaultValue,
      effectiveDefaults: formContext.effectiveDefaults,
    });

  // The input keeps the same position in the tree whichever affordance shows,
  // so React never unmounts it mid-edit and the field holds focus as the user
  // types the very change that hides its sparkle.
  return (
    <JSONSchemaFieldTemplate {...props}>
      <div className="space-y-1">
        <div className="flex items-center gap-1.5">
          <div className="min-w-0 flex-1">{children}</div>
          {reasoning && (
            <PriceModelInputReasoning
              reasoning={reasoning}
              ariaLabel={`Why ${fieldLabel} was set to this value`}
            />
          )}
          {canRevert && (
            <PriceModelInputRevert
              savedValue={readValueAtPath(
                formContext.baselineInputs,
                schemaPath,
              )}
              fieldLabel={fieldLabel}
              onRevert={() => formContext.revertPath(schemaPath)}
            />
          )}
        </div>
        {showsDefaultHint && (
          <p className="text-[11px] leading-tight text-muted-foreground/70">
            Default value - auto-filled by {formContext.priceModelName}
          </p>
        )}
      </div>
    </JSONSchemaFieldTemplate>
  );
}

/**
 * Extracts an agent-authored rationale from a metadata entry.
 * @param entry - The metadata entry for the field, if any
 * @returns The reasoning text, or undefined when the value was not agent-written
 */
function getAgentReasoning(
  entry: PriceModelInputMetadataEntry | undefined,
): string | undefined {
  if (entry?.source.type !== "PRINCIPAL") {
    return undefined;
  }
  if (entry.source.principal.actor?.type !== "agent") {
    return undefined;
  }
  return entry.reasoning?.trim() ? entry.reasoning : undefined;
}

/** The inputs needed to decide whether a field is still at its default. */
interface DefaultCheckArgs {
  schema: JSONSchemaDefinition;
  schemaPath: string[];
  currValue: JSONValue;
  defaultValue: JSONValue;
  effectiveDefaults: JSONObject;
}

/**
 * Reports whether a field still holds the default the price model gives it,
 * whether that default comes from `defaultInputs` or from the field's schema.
 * @param args - The field's schema, path, current value, resolved schema default and the model's effective defaults
 * @returns True when a default exists for the field and the field matches it
 */
function isAtPriceModelDefault(args: DefaultCheckArgs): boolean {
  const { currValue } = args;
  if (currValue === undefined) {
    return false;
  }

  const fieldDefault = resolveFieldDefault(args);
  if (fieldDefault === undefined) {
    return false;
  }
  return JSON.stringify(currValue) === JSON.stringify(fieldDefault);
}

/**
 * Resolves the default a field is expected to hold, preferring the price
 * model's effective defaults over the default synthesised from its schema.
 * @param args - The field's schema, path, resolved schema default and the model's effective defaults
 * @returns The expected default, or undefined when the field declares none
 */
function resolveFieldDefault(args: DefaultCheckArgs): JSONValue {
  const { schema, schemaPath, defaultValue, effectiveDefaults } = args;

  const effectiveDefault = readDefaultAtPath(effectiveDefaults, schemaPath);
  if (effectiveDefault !== undefined) {
    return effectiveDefault;
  }

  // `defaultValue` is synthesised for every field ("", 0, false), so it only
  // stands for a real default when the schema actually declares one.
  if (typeof schema !== "object" || schema === null) {
    return undefined;
  }
  return schema.default === undefined ? undefined : defaultValue;
}

/**
 * Walks the price model's effective defaults by schema path. Only top-level
 * defaults are materialised, so nested and array paths resolve to undefined.
 * @param effectiveDefaults - The price model's effective defaults
 * @param schemaPath - The path addressing the field
 * @returns The default at that path, or undefined when none is defined
 */
function readDefaultAtPath(
  effectiveDefaults: JSONObject,
  schemaPath: string[],
): JSONValue {
  return schemaPath.reduce<JSONValue>(
    (current, segment) =>
      isJSONObject(current) ? current[segment] : undefined,
    effectiveDefaults,
  );
}

/**
 * Reports whether a schema describes a container rather than an input leaf.
 * @param schema - The field's schema
 * @returns True for object and array schemas
 */
function isContainerSchema(schema: JSONSchemaDefinition): boolean {
  if (typeof schema !== "object" || schema === null) {
    return false;
  }
  const type = Array.isArray(schema.type) ? schema.type[0] : schema.type;
  return type === "object" || type === "array";
}
